import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode.react";
import "./auth.scss";
import logo from "@Assets/graphic/logo.png";
import { GoogleIcon, MicrosoftIcon, MailIcon, LockIcon, EyeIcon, EyeOffIcon } from "@Assets/icons";
import { Button, Input } from "antd";
import { useTranslation } from "react-i18next";
import { apiSignIn, apiTwoFactorAuthenticate } from "@Api";
import { IAuth2faResponse, IAuthResponse, Nullable } from "atlas-shared";
import { getApiServerDomain, jwtRefreshToken } from "@Utils";
import { Icon, IconText } from "@Components";
import { useAppDispatch } from "@Store";
import Cookies from "js-cookie";

interface LoginContainerProps {
  redirectTo?: string;
}

export const LoginContainer = React.memo(({ redirectTo }: LoginContainerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Array<string>>();
  const [data, setData] = useState<{ username: string; password: string }>({
    username: "",
    password: "",
  });
  const [twoFa, setTwoFa] = useState<Nullable<IAuth2faResponse>>(null);
  const [twoFaCode, setTwoFaCode] = useState<string>("");

  const setAuthErrors = useCallback(
    (err?: string) => {
      setErrors([t(err || "UNAUTHORIZED")]);
      setLoading(false);
    },
    [t]
  );

  const handleSuccessfulLogin = useCallback(
    (auth: IAuthResponse) => {
      const { user, user_status, tokens } = auth;

      if (redirectTo) {
        const returnUrl = `${redirectTo}${redirectTo.includes("?") ? "&" : "?"}atlas_auth_payload=${
          tokens.access_token
        }`;
        window.location.href = returnUrl;
        return;
      }

      jwtRefreshToken(user, user_status, tokens, dispatch, navigate);
    },
    [redirectTo, dispatch, navigate]
  );

  const login = useCallback(async () => {
    setLoading(true);
    setErrors([]);

    try {
      const auth = await apiSignIn(data.username, data.password);
      if ((auth as IAuth2faResponse).two_factor_auth) {
        setTwoFa(auth as IAuth2faResponse);
      } else {
        handleSuccessfulLogin(auth as IAuthResponse);
      }
    } catch (error) {
      console.log("Error", error);
      setAuthErrors(String(error));
    } finally {
      setLoading(false);
    }
  }, [data.username, data.password, handleSuccessfulLogin, setAuthErrors]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") login();
    },
    [login]
  );

  const handle2FASuccess = useCallback(
    (response: IAuthResponse) => {
      handleSuccessfulLogin(response);
    },
    [handleSuccessfulLogin]
  );

  const handle2FAInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value.length === 6) {
        apiTwoFactorAuthenticate(twoFa!.two_factor_session_token, e.target.value).then(
          handle2FASuccess,
          (err) => setAuthErrors(err.toString())
        );
      } else {
        setErrors([]);
      }
      setTwoFaCode(e.target.value);
    },
    [twoFa, handle2FASuccess, setAuthErrors]
  );

  useEffect(() => {
    const checkAuth = () => {
      const atlasAuth = localStorage.getItem("atlas-auth") || Cookies.get("atlas-auth");

      if (atlasAuth && redirectTo) {
        const returnUrl = `${redirectTo}${
          redirectTo.includes("?") ? "&" : "?"
        }atlas_auth_payload=${atlasAuth}`;
        window.open(returnUrl);
        return;
      }
    };

    checkAuth();
  }, []);

  return (
    <>
      <div className={"login-bg"}></div>
      <div className={"login-wrp"}>
        <div className={"logo-wrp"}>
          <img src={logo} alt={"Logo"} />
        </div>

        <div className={"form-wrp"}>
          {twoFa?.two_factor_auth && (
            <>
              <div className={"form-row"}>
                <span className={"auth-or-txt"}>
                  {t(twoFa.otpauth_url ? "OPEN_AUTHENTICATOR_AND_SCAN" : "OPEN_AUTHENTICATOR")}
                </span>
              </div>
              {twoFa.otpauth_url && !twoFaCode.length && (
                <QRCode value={twoFa.otpauth_url} size={200} style={{ margin: "30px auto" }} />
              )}
              <div className={"form-row"}>
                <div className={"input-wrp"}>
                  <Input
                    size="large"
                    name="auth-code"
                    type="number"
                    placeholder={t("ENTER_6_DIGIT_CODE")}
                    onChange={handle2FAInput}
                    className={"auth-input"}
                    prefix={<LockIcon size={24} />}
                    autoFocus={true}
                  />
                </div>
              </div>
              <div className={"form-row"}>
                <ul className={"form-error-wrp"}>
                  {errors?.map((e) => (
                    <li>{e}</li>
                  ))}
                </ul>
              </div>
            </>
          )}
          {!twoFa && (
            <form>
              <div className={"form-row"}>
                <div className={"social-btn"}>
                  <div
                    className={"social-btn"}
                    onClick={() =>
                      (window.location.href = `${getApiServerDomain()}/auth/google/redirect`)
                    }
                  >
                    <IconText
                      icon={GoogleIcon}
                      iconProps={{ size: 28, stroke: 0, style: { marginRight: "10px" } }}
                      text={t("SIGN_IN_WITH_GOOGLE")}
                    />
                  </div>
                </div>
              </div>
              <div className={"form-row"}>
                <div className={"social-btn"}>
                  <div
                    className={"social-btn"}
                    onClick={() =>
                      (window.location.href = `${getApiServerDomain()}/auth/azure/redirect`)
                    }
                  >
                    <IconText
                      icon={MicrosoftIcon}
                      iconProps={{ size: 28, stroke: 0, style: { marginRight: "10px" } }}
                      text={t("SIGN_IN_WITH_MICROSOFT")}
                    />
                  </div>
                </div>
              </div>
              <div className={"form-row"}>
                <span className={"auth-or-txt"}>{t("OR")}</span>
              </div>
              <div className={"form-row column-dir"}>
                <label className={"form-label"} htmlFor="email">
                  {t("USERNAME")}
                </label>
                <div className={"input-wrp"}>
                  <Input
                    size="large"
                    name="username"
                    onChange={(e) => {
                      const { name, value } = e.target;
                      setData((prev) => ({
                        ...prev,
                        [name]: value,
                      }));
                    }}
                    onKeyDown={handleKeyDown}
                    className={"auth-input"}
                    placeholder=""
                    prefix={<MailIcon size={24} />}
                    autoFocus={true}
                  />
                </div>
              </div>
              <div className={"form-row column-dir"}>
                <label className={"form-label"} htmlFor="email">
                  {t("PASSWORD")}
                </label>
                <div className={"input-wrp"}>
                  <Input.Password
                    type="password"
                    size="large"
                    name="password"
                    onChange={(e) => {
                      const { name, value } = e.target;
                      setData((prev) => ({
                        ...prev,
                        [name]: value,
                      }));
                    }}
                    onKeyDown={handleKeyDown}
                    className={"auth-input"}
                    placeholder=""
                    iconRender={(visible) =>
                      visible ? <Icon icon={EyeIcon} /> : <Icon icon={EyeOffIcon} />
                    }
                    prefix={<LockIcon size={24} />}
                  />
                </div>
              </div>
              {/* <div className={'form-row'] + ' ' + 'flex-start'} >
              <Checkbox checked={rememberMe} className={'checkbox-wrp'} onChange={onChangeRemember}>{t('REMEMBER_ME')}</Checkbox>
            </div> */}
              <div className={"form-row"}>
                <Button
                  loading={loading}
                  onClick={login}
                  className={"sign-submit-btn"}
                  type="primary"
                >
                  {t("SIGN_IN")}
                </Button>
              </div>
              <div className={"form-row"}>
                <ul className={"form-error-wrp"}>
                  {errors?.map((e) => (
                    <li>{e}</li>
                  ))}
                </ul>
              </div>
              <div className={"form-row"}>
                <div
                  className={"forgot-pass clickable"}
                  onClick={() => navigate("/request-reset-password")}
                >
                  {t("FORGOT_YOUR_PASSWORD")}? <span>{t("RESET_PASSWORD")}</span>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
});
